/**
 * Les balises d'en-tête d'une page publique, posées depuis React.
 *
 * ## Pourquoi pas `react-helmet-async`
 *
 * La même raison qui a fait choisir `Intl` plutôt qu'une bibliothèque de
 * fuseaux : le besoin tient en trente lignes, et une dépendance de plus est une
 * dépendance à suivre. Helmet gère des cas que ce site n'a pas — rendu
 * concurrent avec priorités, balises imbriquées, `<body>` dynamique.
 *
 * ## Ce que ça ne fait PAS, et qui compte
 *
 * Ces balises sont posées **après** l'exécution du JavaScript. Un robot qui ne
 * rend pas la page ne les voit jamais : il reçoit le `<head>` statique de
 * `index.html`. C'est la raison d'être du pré-rendu au build — sans lui, ce
 * fichier n'améliore le référencement que pour les robots qui exécutent le JS,
 * c'est-à-dire Googlebot et lui seul avec constance.
 *
 * Dit autrement : ce module rend les pages correctes ; le pré-rendu les rend
 * visibles. Les deux sont nécessaires, et celui-ci vient d'abord parce que le
 * pré-rendu ne fait que figer ce qu'il produit.
 */

import { useEffect } from 'react';

/** L'origine publique du catalogue. Les URL canoniques sont absolues. */
export const ORIGINE = 'https://app.eclosion.be';

export interface EnTete {
  titre: string;
  description: string;
  /** Chemin absolu depuis la racine, par exemple `/stages/velo-forest-ete-2026`. */
  chemin: string;
  image?: string | null;
  /** `false` sur une page qui ne mérite pas d'être indexée (filtre, résultat vide). */
  indexable?: boolean;
  /** Les données structurées de la page, déjà construites. */
  donnees?: unknown;
}

/** Pose (ou remplace) une balise `<meta>` identifiée par `name` ou `property`. */
function poserMeta(cle: 'name' | 'property', valeur: string, contenu: string) {
  let balise = document.head.querySelector<HTMLMetaElement>(`meta[${cle}="${valeur}"]`);
  if (!balise) {
    balise = document.createElement('meta');
    balise.setAttribute(cle, valeur);
    document.head.appendChild(balise);
  }
  balise.setAttribute('content', contenu);
}

function poserLien(rel: string, href: string) {
  let balise = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!balise) {
    balise = document.createElement('link');
    balise.setAttribute('rel', rel);
    document.head.appendChild(balise);
  }
  balise.setAttribute('href', href);
}

/**
 * L'identifiant du bloc de données structurées géré par ce module.
 *
 * Il est nommé pour que le changement de page remplace le bloc précédent au
 * lieu d'en empiler un second : deux `Event` sur une page, un moteur ne sait
 * pas lequel décrit ce qu'il lit.
 */
const ID_DONNEES = 'donnees-structurees-de-la-page';

/** L'image de partage quand la page n'en a pas : celle d'`index.html`. */
const IMAGE_PAR_DEFAUT = '/icone-eclosion.png';

export function useEnTete({
  titre,
  description,
  chemin,
  image,
  indexable = true,
  donnees,
}: EnTete): void {
  useEffect(() => {
    const url = `${ORIGINE}${chemin}`;

    document.title = titre;
    poserMeta('name', 'description', description);
    poserLien('canonical', url);

    poserMeta('property', 'og:title', titre);
    poserMeta('property', 'og:description', description);
    poserMeta('property', 'og:url', url);
    poserMeta('property', 'og:type', 'website');
    /*
      Toujours posee, avec un repli. Ne l'ecrire que lorsqu'il y a une photo
      laissait celle de la page precedente en place : le `<head>` est partage,
      et un stage sans photo heritait de celle du stage d'avant.

      Les dimensions ne sont declarees que pour l'icone, dont on les connait :
      une taille fausse fait dessiner une carte de travers, et la photo d'un
      stage n'a pas de taille connue d'ici.
    */
    poserMeta('property', 'og:image', image || `${ORIGINE}${IMAGE_PAR_DEFAUT}`);
    for (const cote of ['width', 'height'] as const) {
      const balise = document.head.querySelector(`meta[property="og:image:${cote}"]`);
      if (image) balise?.remove();
      else if (!balise) poserMeta('property', `og:image:${cote}`, '512');
    }

    /*
      `noindex` est posé explicitement plutôt que laissé absent : une page de
      filtre sans résultat doit dire qu'elle ne vaut pas d'être indexée, sinon
      elle le sera. On retire la balise quand la page redevient indexable —
      sans quoi le `noindex` d'une page survivrait à la navigation vers une
      autre, le `<head>` étant partagé.
    */
    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (indexable) {
      robots?.remove();
    } else {
      poserMeta('name', 'robots', 'noindex, follow');
    }

    const ancien = document.getElementById(ID_DONNEES);
    ancien?.remove();
    if (donnees) {
      const script = document.createElement('script');
      script.id = ID_DONNEES;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(donnees);
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(ID_DONNEES)?.remove();
    };
  }, [titre, description, chemin, image, indexable, donnees]);
}
